import { logger } from "@/util/logger";

const DEVICE_MISSING_ERROR_NAMES = new Set([
  "NotFoundError",
  "DevicesNotFoundError",
  "OverconstrainedError",
  "ConstraintNotSatisfiedError",
]);

export const isMediaDeviceMissingError = (error: unknown): boolean => {
  const name = (error as { name?: string } | null)?.name;
  return Boolean(name && DEVICE_MISSING_ERROR_NAMES.has(name));
};

const hasExactDeviceId = (
  constraint: boolean | MediaTrackConstraints | undefined
): boolean => {
  if (!constraint || constraint === true) return false;
  const deviceId = constraint.deviceId;
  if (!deviceId) return false;
  if (typeof deviceId === "string") return deviceId.length > 0;
  if (typeof deviceId === "object") {
    if ("exact" in deviceId) {
      const exact = deviceId.exact;
      return Array.isArray(exact) ? exact.length > 0 : Boolean(exact);
    }
  }
  return false;
};

const stripExactDeviceId = (
  constraint: boolean | MediaTrackConstraints | undefined
): boolean | MediaTrackConstraints | undefined => {
  if (constraint === undefined || typeof constraint === "boolean") {
    return constraint;
  }
  const { deviceId: _deviceId, ...rest } = constraint;
  return Object.keys(rest).length > 0 ? rest : true;
};

/**
 * getUserMedia that retries without `deviceId: { exact }` when the preferred
 * device is gone (unplugged mic, stale saved id). Rethrows NotAllowedError
 * and true "no devices at all" failures after the fallback.
 */
export const getUserMediaWithDeviceFallback = async (
  constraints: MediaStreamConstraints
): Promise<MediaStream> => {
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    const canFallback =
      isMediaDeviceMissingError(error) &&
      (hasExactDeviceId(constraints.audio) ||
        hasExactDeviceId(constraints.video));

    if (!canFallback) throw error;

    const fallback: MediaStreamConstraints = {
      audio: stripExactDeviceId(constraints.audio),
      video: stripExactDeviceId(constraints.video),
    };

    if (!fallback.audio && !fallback.video) throw error;

    logger.log(
      "getUserMedia preferred device failed; retrying without exact deviceId",
      {
        name: (error as Error)?.name,
        message: (error as Error)?.message,
      }
    );

    return await navigator.mediaDevices.getUserMedia(fallback);
  }
};
